import { useMemo, useState } from "react";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilterBar from "@/components/feature/FilterBar";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import { allMovies } from "@/utils/catalog";
import type { Movie } from "@/types";

const PAGE_SIZE = 6;

function toOptions(values: string[]) {
  return [{ label: "全部", value: "全部" }, ...values.map((v) => ({ label: v, value: v }))];
}

export default function MovieCategoryPage() {
  const [filters, setFilters] = useState<Record<string, string>>({
    genre: "全部",
    region: "全部",
    year: "全部",
  });
  const [sort, setSort] = useState("release");
  const [page, setPage] = useState(1);

  const genres = useMemo(
    () => toOptions(Array.from(new Set(allMovies.map((m) => m.genre)))),
    []
  );
  const regions = useMemo(
    () => toOptions(Array.from(new Set(allMovies.map((m) => m.region)))),
    []
  );
  const years = useMemo(
    () =>
      toOptions(
        Array.from(new Set(allMovies.map((m) => String(m.year)))).sort((a, b) => Number(b) - Number(a))
      ),
    []
  );

  const filtered = useMemo(() => {
    let list: Movie[] = allMovies.filter((m) => {
      const okGenre = filters.genre === "全部" || m.genre === filters.genre;
      const okRegion = filters.region === "全部" || m.region === filters.region;
      const okYear = filters.year === "全部" || String(m.year) === filters.year;
      return okGenre && okRegion && okYear;
    });
    list = [...list].sort((a, b) =>
      sort === "heat"
        ? b.heat - a.heat
        : new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime()
    );
    return list;
  }, [filters, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({ genre: "全部", region: "全部", year: "全部" });
    setSort("release");
    setPage(1);
  };

  return (
    <div className="w-full">
      <div className="w-full px-4 md:px-8 pt-6 pb-2">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影视" }]} />
      </div>

      <div className="w-full px-4 md:px-8 pt-4 pb-10 md:pb-14">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-9 h-9 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center">
              <i className="ri-film-line text-lg"></i>
            </span>
            <h1 className="font-heading text-2xl md:text-3xl font-black text-foreground-950">
              影视分类
            </h1>
          </div>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl">
            汇聚科幻、悬疑、奇幻、年代剧情等热门影视剧集，支持按类型、地区、年份筛选，快速找到你想看的内容。
          </p>
        </div>

        <FilterBar
          groups={[
            { key: "genre", label: "类型", options: genres },
            { key: "region", label: "地区", options: regions },
            { key: "year", label: "年份", options: years },
          ]}
          values={filters}
          onChange={handleFilterChange}
          sorts={[
            { label: "按更新时间", value: "release" },
            { label: "按热度", value: "heat" },
          ]}
          sortValue={sort}
          onSortChange={(v) => {
            setSort(v);
            setPage(1);
          }}
        />

        <div className="mt-5 mb-4 text-sm text-foreground-500">
          共找到 <span className="font-medium text-primary-600">{filtered.length}</span> 部影视作品
        </div>

        {paged.length > 0 ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-5">
              {paged.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
            <div className="mt-10">
              <Pagination current={currentPage} total={totalPages} onChange={setPage} />
            </div>
          </>
        ) : (
          <EmptyState
            title="暂无符合条件的影视内容"
            description="试试调整筛选条件，或点击下方按钮重置全部筛选。"
            actionLabel="重置筛选条件"
            onAction={resetFilters}
          />
        )}
      </div>
    </div>
  );
}